import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { AiOutlineDownload, AiOutlineInfo } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import EvmcFiltersWrapper from "../../components/filter-widget/evmcFiltersWrapper";
import TableComponent, { DataTable } from "../../components/table-widget/tableComponent";
import DataDisplaySwitcher from "../../components/templates/components/dataDisplaySwitcher";
import SmallFilterAccessor from "../../components/templates/components/smallFilterAccessor";
import PageTemplate from "../../components/templates/pageTemplate";
import { EvmcType, ReferenceSelection } from "../../models/EvmcSelections";
import { SelectedAge } from "../../models/SelectedAge";
import { States } from "../../redux";
import { getEVMCDisplayMode } from "../../redux/selectors/displayModeSelector";
import { getEvmcCategoryAgeFilterState, getEvmcCategorySocialCategoryFilterState, getEvmcEducationAgeFilterState, getEvmcEducationEducationFilterState, getEvmcLivingAgeFilterState, getEvmcLivingLivingFilterState } from "../../redux/selectors/filterSelector";
import { DisplayModeAction } from "../../redux/states/displayModeState";
import getData from "../../service/dataLoader";
import { useWindowWidthAndHeight } from "../../shared/hooks/customHooks";
import { convertNumberToString } from "../../shared/utils/numbersUtils";
import EvmcGenderLine, { EvmcGenderData } from "./evmcGenderLine";

interface EvmcLine {
  gender: string;
  age: string;
  category: string;
  value: string;
}

interface EvmcData {
  living: EvmcLine[];
  education: EvmcLine[];
  category: EvmcLine[];
}

const emptyEvmcData: EvmcData = {
  living: [],
  education: [],
  category: [],
};

const getAgeFilter = (type: EvmcType) => {
  switch (type) {
    case EvmcType.CATEGORY:
      return getEvmcCategoryAgeFilterState;
    case EvmcType.EDUCATION:
      return getEvmcEducationAgeFilterState;
    default:
      return getEvmcLivingAgeFilterState;
  }
};
const getCategoryFilter = (type: EvmcType): ((s: States) => ReferenceSelection<string>) => {
  switch (type) {
    case EvmcType.CATEGORY:
      return getEvmcCategorySocialCategoryFilterState;
    case EvmcType.EDUCATION:
      return getEvmcEducationEducationFilterState;
    default:
      return getEvmcLivingLivingFilterState;
  }
};
const getCsvUrl = (type: EvmcType) => {
  switch (type) {
    case EvmcType.CATEGORY:
      return `en/outil-interactif/7344233/data/esperance_vie_categorie_sociale.csv`;
    case EvmcType.EDUCATION:
      return `en/outil-interactif/7344233/data/esperance_vie_diplome.csv`;
    default:
      return `en/outil-interactif/7344233/data/esperance_vie_niveau_vie.csv`;
  }
};

const getXlsxUrl = (type: EvmcType) => {
  switch (type) {
    case EvmcType.CATEGORY:
      return `en/outil-interactif/7344233/data/downloads/Esperance_Vie_CATEGORIESOCIALE.xlsx`;
    case EvmcType.EDUCATION:
      return `en/outil-interactif/7344233/data/downloads/Esperance_Vie_DIPLOME.xlsx`;
    default:
      return `en/outil-interactif/7344233/data/downloads/Esperance_Vie_NIVEAUDEVIE.xlsx`;
  }
};

const EvmcWidget: FC<{ type: EvmcType }> = ({ type }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<EvmcData>(emptyEvmcData);
  const dispatch = useDispatch();
  const [width] = useWindowWidthAndHeight();

  const ageFilter = useSelector(getAgeFilter(type));
  const categoryFilter = useSelector(getCategoryFilter(type));
  const displayMode = useSelector(getEVMCDisplayMode);
  const [tooltipId, setTooltipId] = useState<string>(displayMode.isChartMode ? "tableDisplayMode" : "chartDisplayMode");

  useEffect(() => {
    if (data[type].length === 0) {
      getData(getCsvUrl(type)).then((lines) => {
        setData({ ...data, [type]: lines.data as EvmcLine[] });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    setTooltipId(displayMode.isChartMode ? "chartDisplayMode" : "tableDisplayMode");
  }, [displayMode.isChartMode]);

  const ageLabel = ageFilter.seletedAge === SelectedAge.birth ? t("evmc-birth") : t("evmc-age", { count: ageFilter.customSelectedAge });

  const getValue = (gender: "H" | "F", category: string): number => Number.parseFloat(data[type].find((d) => d.gender === gender && Number.parseInt(d.age) === ageFilter.customSelectedAge && d.category === category)?.value || "0");
  const femaleData: EvmcGenderData = {
    ref: getValue("F", categoryFilter.ref),
    secondary: getValue("F", categoryFilter.secondary),
  };
  const maleData: EvmcGenderData = {
    ref: getValue("H", categoryFilter.ref),
    secondary: getValue("H", categoryFilter.secondary),
  };

  const onSetDisplayModeEvent = () => {
    dispatch(DisplayModeAction.changeEVMCDisplayMode());
  };

  const tableHeaders = () => [t("evmc-table-header-gender"), t(`evmc-filters-${categoryFilter.ref}`), t(`evmc-filters-${categoryFilter.secondary}`), t("evmc-table-header-delta")];

  const tableData = (): DataTable[] => [
    {
      key: "gender-F",
      columns: [
        { data: t("women"), component: "th" },
        { data: convertNumberToString(femaleData.ref, i18n, 1, 1) },
        { data: convertNumberToString(femaleData.secondary, i18n, 1, 1) },
        { data: convertNumberToString(Math.abs(Math.round((femaleData.ref - femaleData.secondary) * 10) / 10), i18n, 1, 1) },
      ],
    },
    {
      key: "gender-M",
      columns: [{ data: t("men"), component: "th" }, { data: convertNumberToString(maleData.ref, i18n, 1, 1) }, { data: convertNumberToString(maleData.secondary, i18n, 1, 1) }, { data: convertNumberToString(Math.abs(Math.round((maleData.ref - maleData.secondary) * 10) / 10), i18n, 1, 1) }],
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t(`evmc-${type}-title`)}</title>
      </Helmet>
      <PageTemplate
        evmc
        pageName="France"
        dataSource={t(`evmc-${type}-data-source`)}
        graphContent={
          <div className="display-data" aria-label={t("charts_aria-label") || ""}>
            <div className="display-data-header-evmc">
              <h3>
                {t(`evmc-${type}-graph-title`, {
                  replace: { age: ageLabel },
                })}
              </h3>

              <div className="graph-button-container">
                {width < 1150 ? <SmallFilterAccessor></SmallFilterAccessor> : undefined}

                <DataDisplaySwitcher
                  tooltipId="displayMode"
                  isChartMode={displayMode.isChartMode}
                  setModeHandler={onSetDisplayModeEvent}
                ></DataDisplaySwitcher>

                <span
                  aria-label="Informations supplémentaires sur les données d'espérance de vie par milieu social"
                  aria-details="infoTip"
                  data-tip
                  data-for="infoTip"
                  className="info-round-button"
                >
                  <AiOutlineInfo style={{ color: "#0C3A5A" }}></AiOutlineInfo>
                </span>

                <a
                  href={getXlsxUrl(type)}
                  aria-label={t("download-aria-label") || ""}
                  data-tip
                  data-for="downloadTip"
                  className="download-round-button"
                >
                  <AiOutlineDownload style={{ color: "#ffffff" }}></AiOutlineDownload>
                </a>

                <ReactTooltip id="infoTip" place="bottom" effect="solid" multiline={true} html={true}>
                  {t("tooltip-info-evmc")}
                </ReactTooltip>
                <ReactTooltip id="downloadTip" place="bottom" effect="solid" multiline={true} html={true}>
                  {t("tooltip-download-evmc")}
                </ReactTooltip>
                <ReactTooltip id="displayMode" place="bottom" effect="solid" multiline={true} html={true}>
                  {tooltipId === "chartDisplayMode" ? t("tooltip-change-display-table-mode") : t("tooltip-change-display-chart-mode")}
                </ReactTooltip>
              </div>
            </div>
            {displayMode.isChartMode ? (
              <>
                <EvmcGenderLine
                  data={femaleData}
                  gender="F"
                  refCategory={categoryFilter.ref}
                  secondaryCategory={categoryFilter.secondary}
                  minAge={Math.min(femaleData.ref, femaleData.secondary, maleData.ref, maleData.secondary)}
                  maxAge={Math.max(femaleData.ref, femaleData.secondary, maleData.ref, maleData.secondary)}
                />
                <EvmcGenderLine
                  data={maleData}
                  gender="H"
                  refCategory={categoryFilter.ref}
                  secondaryCategory={categoryFilter.secondary}
                  minAge={Math.min(femaleData.ref, femaleData.secondary, maleData.ref, maleData.secondary)}
                  maxAge={Math.max(femaleData.ref, femaleData.secondary, maleData.ref, maleData.secondary)}
                />
              </>
            ) : (
              <>
                <TableComponent
                  caption={t(`evmc-${type}-graph-title`, { replace: { age: ageLabel } })}
                  unit={t("year_unit_plural", { postProcess: "plural" })}
                  data={tableData()}
                  titles={tableHeaders()}
                />
                <span className="sidenote">{t("evmc-table-header-gap-note")}</span>
              </>
            )}
          </div>
        }
        filterContent={<EvmcFiltersWrapper type={type} />}
      />
    </>
  );
};

export default EvmcWidget;
